/**
 * NiceScale utility class for generating "nice" axis values
 * Based on "Graphics Gems, Volume 1" by Andrew S. Glassner
 */
export class NiceScale {
  private minPoint: number;
  private maxPoint: number;
  private maxTicks: number;
  private tickSpacing!: number; // Using definite assignment assertion
  private range!: number;
  private niceMin!: number;
  private niceMax!: number;

  /**
   * Creates a new NiceScale instance
   *
   * @param min The minimum data point on the axis
   * @param max The maximum data point on the axis
   * @param maxTicks The maximum number of ticks (default: 10)
   */
  constructor(min: number, max: number, maxTicks: number = 10) {
    this.minPoint = min;
    this.maxPoint = max;
    this.maxTicks = maxTicks;
    this.calculate();
  }

  /**
   * Calculate and update values for tick spacing and nice
   * minimum and maximum data points on the axis.
   */
  private calculate(): void {
    this.range = this.niceNum(this.maxPoint - this.minPoint, false);
    this.tickSpacing = this.niceNum(this.range / (this.maxTicks - 1), true);
    this.niceMin =
      Math.floor(this.minPoint / this.tickSpacing) * this.tickSpacing;
    this.niceMax =
      Math.ceil(this.maxPoint / this.tickSpacing) * this.tickSpacing;
  }

  /**
   * Returns a "nice" number approximately equal to range.
   * Rounds the number if round = true, takes the ceiling if round = false.
   *
   * @param range The data range
   * @param round Whether to round the result
   * @returns A "nice" number to be used for the data range
   */
  private niceNum(range: number, round: boolean): number {
    // Handle edge cases for zero or very small range
    if (range === 0 || !isFinite(range)) {
      return 0.01;
    }

    const exponent = Math.floor(Math.log10(range));
    const fraction = range / Math.pow(10, exponent);
    let niceFraction: number;

    if (round) {
      if (fraction < 1.5) niceFraction = 1;
      else if (fraction < 3) niceFraction = 2;
      else if (fraction < 7) niceFraction = 5;
      else niceFraction = 10;
    } else {
      if (fraction <= 1) niceFraction = 1;
      else if (fraction <= 2) niceFraction = 2;
      else if (fraction <= 5) niceFraction = 5;
      else niceFraction = 10;
    }

    return niceFraction * Math.pow(10, exponent);
  }

  /**
   * Returns the tick spacing
   */
  public getTickSpacing(): number {
    return this.tickSpacing;
  }

  /**
   * Returns the nice minimum value
   */
  public getNiceMin(): number {
    return this.niceMin;
  }

  /**
   * Returns the nice maximum value
   */
  public getNiceMax(): number {
    return this.niceMax;
  }

  /**
   * Returns an array of tick values
   */
  public getTicks(): number[] {
    const ticks: number[] = [];
    let tick = this.niceMin;

    // Add each tick value to the array
    while (tick <= this.niceMax) {
      // Push the tick and round to avoid floating-point precision issues
      ticks.push(parseFloat(tick.toFixed(10)));
      tick += this.tickSpacing;
    }

    return ticks;
  }

  /**
   * Sets new min and max points
   */
  public setMinMaxPoints(minPoint: number, maxPoint: number): void {
    this.minPoint = minPoint;
    this.maxPoint = maxPoint;
    this.calculate();
  }

  /**
   * Sets new max ticks
   */
  public setMaxTicks(maxTicks: number): void {
    this.maxTicks = maxTicks;
    this.calculate();
  }
}
